import WDListForm from '../lists/wd-list-form';
import WDListFormItem from '../lists/wd-list-form-item';
import WDUtil from '../../core/wd-util';
import WDButton from '../button/wd-button';
import WDDropdownForm from '../dropdown/wd-dropdown-form';
import { WDFormText } from '../form/wd-form-text';
import { WDFormHidden } from '../form/wd-form-hidden';
import * as WDForms from '../form/index-forms';

import './wd-filter-matches';

/**
 * WDFilter - A list-based filter component
 * Extends WDListForm to provide dynamic filtering capabilities
 */
export default class WDFilter extends WDListForm
{
	#joinForm = null;
	#parent = null;
	#filterConfig;

	constructor(cfg, parent)
	{
		// Extract fields as plain objects using WDUtil.merge to handle WDModel proxies
		const filterConfig = WDUtil.merge({},cfg);
		const fieldOpts = filterConfig.fields.map(x => ({label:x.label, value:x.name}));

		const listConfig = {
			rows: {
				allowAdd: true,
				allowRemove: true,
				forms: [
					{
						name: 'field',
						type: 'dropdown',
						options: fieldOpts,
						allowClear: false,
						wrapper_class: 'col-12',
					},
					// Match selector
					{
						name: 'match',
						type: 'dropdown',
						options: [{label:'equals',value:'equals'}],
						allowClear: false,
						wrapper_class: 'col-12',
						value: 'equals'
					},

				], // Forms are added dynamically per item
			},
			model: {
				data: null,
				initFetch: false
			},
			showHeader: true,
		};

		super(listConfig);

		this.classList.remove('wd-list-form');
		this.classList.add('wd-filter');

		// Store filter-specific config with plain field objects
		this.#parent = parent;
		this.#filterConfig = {
			fields: filterConfig.fields, // Use the plain objects we extracted above
			join: filterConfig.join,
			joinHidden: filterConfig.joinHidden,
			allowGroups: filterConfig.allowGroups
		};

		// Add join form to header
		this.buildJoinForm();
	}

	connectedCallback()
	{
		super.connectedCallback();

		this.model.on('change', () =>
		{
			this.trigger('value:set');
		});

	}

	buildJoinForm()
	{
		const joinHidden = this.#filterConfig?.joinHidden || false;
		const joinValue = this.#filterConfig?.join || 'and';

		if (joinHidden)
		{
			this.#joinForm = new WDFormHidden({ name: 'join', value: joinValue });
		}
		else
		{
			const matchLabel = wdc('<div class="join-label">Match</div>');
			const cfg = { 
				allowClear: false, 
				name: 'join', 
				options: [
					{ label: 'All', value: 'and' }, 
					{ label: 'Any', value: 'or' }
				], 
				value: joinValue 
			};

			this.#joinForm = new WDDropdownForm(cfg);
			
			// Add to header prefix (before buttons)
			this.header.prefix.append(matchLabel);
			this.header.prefix.append(this.#joinForm);
			this.header.suffix.replaceChildren();

			if (this.#filterConfig.allowGroups) this.addGroupButton();
		}
	
	}

	addGroupButton()
	{
		const addBtn = new WDButton({ label: 'Add Filter Group', fasIcon: 'object-group', theme: 'primary', size: 'sm', click: () => this.addFilterGroup() });
		this.header.suffix.append(addBtn);

		// Add remove button if this is a nested filter
		if (this.#parent)
		{
			const removeBtn = new WDButton({
				theme: 'clear',
				icon: 'fa-regular fa-trash-alt'
			});
			removeBtn.classList.add('ms-auto');
			removeBtn.on('click', () =>
			{
				this.remove();
				//this.trigger('value:set');
			});
			this.header.suffix.append(removeBtn);
		}
	}

	// Override to create filter-specific items
	createListItem(data)
	{
		// Check if this is a nested 
		// filter group
		if (data?.fields !== undefined)
		{
			const f = new WDFilter(data,this);
			//this.bubbleEvents(f,['value:set']);
			return f;
		}
		else
		{
			const item = new WDFilterItem(this.config.rows, this.#filterConfig.fields);
			item.model = data;
			
			//item.on('value:set',evt => this.trigger('value:set') );
			return item;
		}
	}

	// Add a nested filter group
	addFilterGroup()
	{
		const position = this.items.length;
		this.model.insert(position, this.#filterConfig);
	}

	// Override value getter to return filter-specific structure
	get value()
	{
		const val = {
			join: this.#joinForm.value,
			filters: this.model.toArray(),
		};
		
		return val;
	}

	// Override value setter to handle filter-specific structure
	set value(data)
	{
		if (!data) return;

		// Set join value
		if (data.join) this.#joinForm.value = data.join;

		if (data.filters && Array.isArray(data.filters))
		{
			this.model = data.filters;
		}

		//this.trigger('value:set');
	}

	filterHasValue(filterData)
	{
		// For nested groups, check if they have filters
		if (filterData.filters !== undefined)
		{
			return filterData.filters.length > 0;
		}

		// For regular filters, check if field and value are set
		return filterData.field && 
		       (filterData.value !== null && 
		        filterData.value !== undefined && 
		        String(filterData.value).trim().length > 0);
	}

	cleanupCallback()
	{
		if (this.#joinForm)
		{
			this.#joinForm = null;
		}

		this.#parent = null;
		this.#filterConfig = null;

		super.cleanupCallback();
	}
}

/**
 * WDFilterItem - A single filter row
 * Contains field/match/value form controls
 * Leverages WDListFormItem's form management via config.rows.forms
 */
class WDFilterItem extends WDListFormItem
{
	#filterFields = [];

	constructor(cfg, filterFields)
	{	
		super(cfg);

		this.classList.add('wd-filter-item');
		this.#filterFields = filterFields;

	}

	connectedCallback()
	{
		super.connectedCallback();

		this.model.on('change',() => {
			this.trigger('value:set');
			//console.log('Filter Item Model Change');
		});

	}

	buildContent()
	{
		super.buildContent();

		this.getItem('field').on('change',(evt) => this.handleFieldChange(evt));

		if (!this.model.field)
		{
			this.getItem('field').value = this.#filterFields[0].name;
		}

		this.handleFieldChange();

		//now that we have content, watch for changes
		this.model.on('set',() => this.trigger('value:set'));
	}

	getFormConfigForField(fieldName)
	{
		let formConfig = { type: 'text' };

		const found = this.#filterFields.find(x => x.name === fieldName);
		if (found) formConfig = found;

		return WDUtil.merge({}, formConfig);
	}

	handleFieldChange(evt)
	{
		const fieldForm = this.getItem('field');
		
		if (!fieldForm) return;

		const fieldName = fieldForm.value;
		const formConfig = this.getFormConfigForField(fieldName, this.#filterFields);

		this.updateMatchForm(formConfig);
		this.updateValueForm(formConfig);
	}

	updateMatchForm(formConfig)
	{
		const matchForm = this.getItem('match');

		const matchOpts = formConfig.matches || WDFilterMatches[formConfig.type] || WDFilterMatches.text;

		// Update options using the dropdown's method
		if (matchForm.options !== undefined)
		{
			matchForm.options = matchOpts;

			// Reset to first match option
			if (matchOpts.length > 0)
			{
				matchForm.value = matchOpts[0].value;
			}
		}

	}

	updateValueForm(formConfig)
	{
		let valueCell = this.querySelector('.wd-list-item-cell.value');

		const newFormConfig = WDUtil.merge(formConfig, {
			name: 'value',
			value: null,
			wrapper_class: 'col-12'
		});

		delete newFormConfig.label;
		if (!valueCell) 
		{
			valueCell = this.add(newFormConfig);
			valueCell.classList.add('value');
		}
		else
		{
			const formItem = valueCell.querySelector('.wd-form-item');
			if (formItem) formItem.remove();

			const newForm = this.form.buildForm(newFormConfig);
			valueCell.append(newForm);
		}

	}

	get value()
	{
		// Use form's value getter which aggregates all form fields
		return this.form.value;
	}

	set value(data)
	{
		if (!data) return;

		// Use form's value setter which distributes to all fields
		this.form.value = data;

	}

	cleanupCallback()
	{
		super.cleanupCallback();
		this.#filterFields = null;
	}
}

customElements.define('wd-filter', WDFilter);
customElements.define('wd-filter-item',WDFilterItem);